<script setup lang="ts">
import {
  clearDeferredBeforeInstallPrompt,
  getDeferredBeforeInstallPrompt
} from '@/lib/pwaBeforeInstallPrompt.ts';
import {
  eq,
  gt,
  includes,
  isEmpty,
  isFinite,
  isFunction,
  isNil,
  lt,
  some,
  subtract,
  toLower
} from 'lodash';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

defineOptions({ name: 'PwaInstallBanner' });

const { t } = useI18n();

const DISMISS_KEY = 'pwa_install_dismissed_at';
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/** https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgentData (Chromium; not in lib.dom for all TS versions) */
interface NavigatorWithUaData extends Navigator {
  userAgentData?: {
    getHighEntropyValues?(hints: string[]): Promise<{ platform?: string }>;
  };
}

const deferredPrompt = getDeferredBeforeInstallPrompt();

const visible = ref(false);
const mode = ref<'android' | 'ios' | null>(null);

const bannerTitle = computed(() => {
  if (mode.value === 'android') {
    return t('pwa.install.titleAndroid');
  }
  if (mode.value === 'ios') {
    return t('pwa.install.titleIos');
  }
  return '';
});

const androidSubtitle = computed(() => t('pwa.install.subtitleAndroid'));
const iosDisclaimer = computed(() => t('pwa.install.iosDisclaimer'));

function readDismissedAt(): number | null {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (isEmpty(raw)) {
    return null;
  }
  const n = Number(raw);
  return isFinite(n) ? n : null;
}

function isDismissedWithin30Days(): boolean {
  const at = readDismissedAt();
  if (isNil(at)) {
    return false;
  }
  return lt(subtract(Date.now(), at), THIRTY_DAYS_MS);
}

function persistDismiss(): void {
  localStorage.setItem(DISMISS_KEY, String(Date.now()));
}

function isStandaloneDisplay(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches;
}

function isIosWebAppStandalone(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return eq(nav.standalone, true);
}

function isInstalledLike(): boolean {
  return isStandaloneDisplay() || isIosWebAppStandalone();
}

/**
 * Android phones and tablets: Chromium fires `beforeinstallprompt` when the app
 * meets installability criteria — we use that + `prompt()` (no UA sniffing).
 *
 * iPhone / iPad: Apple does not expose `BeforeInstallPromptEvent`. Add to Home Screen
 * is only offered from the browser’s own Share control (toolbar), not from
 * `navigator.share()` — we only show instructions + dismiss on iOS.
 *
 * Detection order: exclude Android first, then `navigator.userAgentData` high‑entropy
 * `platform` when available (Client Hints), then legacy signals for Safari.
 */
const IOS_UA_DEVICE_MARKERS = ['ipad', 'iphone', 'ipod'] as const;

async function isAppleMobileDevice(): Promise<boolean> {
  const uaLower = toLower(navigator.userAgent);
  if (includes(uaLower, 'android')) {
    return false;
  }

  const uaData = (navigator as NavigatorWithUaData).userAgentData;
  if (isFunction(uaData?.getHighEntropyValues)) {
    try {
      const hints = await uaData.getHighEntropyValues(['platform']);
      if (eq(hints.platform, 'iOS')) {
        return true;
      }
    } catch {
      // Fall through to legacy detection (e.g. Safari with no CH response).
    }
  }

  if (some(IOS_UA_DEVICE_MARKERS, marker => includes(uaLower, marker))) {
    return true;
  }

  return eq(navigator.platform, 'MacIntel') && gt(navigator.maxTouchPoints, 1);
}

function onDismiss(): void {
  persistDismiss();
  clearDeferredBeforeInstallPrompt();
  visible.value = false;
  mode.value = null;
}

async function onInstall(): Promise<void> {
  const promptEvent = deferredPrompt.value;
  if (isNil(promptEvent)) {
    return;
  }
  try {
    await promptEvent.prompt();
    await promptEvent.userChoice;
  } catch {
    // User dismissed the system prompt or prompt is unavailable.
  }
  onDismiss();
}

watch(
  () => deferredPrompt.value,
  prompt => {
    if (isDismissedWithin30Days() || isInstalledLike()) {
      return;
    }
    if (!isNil(prompt)) {
      mode.value = 'android';
      visible.value = true;
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (isDismissedWithin30Days() || isInstalledLike()) {
    return;
  }
  void (async () => {
    if (!isNil(deferredPrompt.value)) {
      return;
    }
    if (await isAppleMobileDevice()) {
      if (isDismissedWithin30Days() || isInstalledLike()) {
        return;
      }
      if (!isNil(deferredPrompt.value)) {
        return;
      }
      mode.value = 'ios';
      visible.value = true;
    }
  })();
});
</script>

<template>
  <v-sheet
    v-if="visible"
    border="b"
    class="d-flex align-center justify-space-between px-4 py-2 flex-shrink-0"
    color="amber-lighten-5"
    elevation="0"
    rounded="0">
    <div class="d-flex align-center gap-3 min-width-0">
      <v-icon
        class="flex-shrink-0 text-medium-emphasis"
        icon="mdi-cellphone-arrow-down-variant"
        size="20" />
      <div class="min-width-0">
        <div class="text-body-2 font-weight-semibold text-high-emphasis">{{ bannerTitle }}</div>
        <div v-if="mode === 'android'" class="text-caption text-medium-emphasis">
          {{ androidSubtitle }}
        </div>
        <div v-else-if="mode === 'ios'" class="text-caption text-medium-emphasis">
          {{ iosDisclaimer }}
        </div>
      </div>
    </div>
    <div v-if="mode === 'android'" class="d-flex align-center gap-2 flex-shrink-0 ml-3">
      <v-btn
        class="text-black text-body-2 font-weight-bold"
        color="primary"
        size="small"
        variant="flat"
        @click="onInstall">
        {{ t('pwa.install.install') }}
      </v-btn>
      <v-btn
        class="text-body-2 font-weight-bold text-medium-emphasis"
        size="small"
        variant="text"
        @click="onDismiss">
        {{ t('pwa.install.notNow') }}
      </v-btn>
    </div>
    <div v-else-if="mode === 'ios'" class="d-flex align-center flex-shrink-0 ml-2">
      <v-btn
        :aria-label="t('pwa.install.dismissHint')"
        class="text-medium-emphasis"
        density="comfortable"
        icon="mdi-close"
        size="small"
        variant="text"
        @click="onDismiss" />
    </div>
  </v-sheet>
</template>

<style scoped lang="scss">
.min-width-0 {
  min-width: 0;
}
</style>
